/* eslint-disable jsdoc/require-jsdoc -- 内部边界守卫保持小函数，公开映射入口已有说明。 */
import type { components } from "../../../api/generated";
import type {
  HotelMapCoordinate,
  HotelMapGeoVerificationStatus,
  HotelMapPoi,
  HotelMapSearchItem,
  HotelMapSearchResult,
  HotelMapViewport,
} from "../types";

export type StayMapSearchResultRaw = components["schemas"]["StayResourceMapSearchResult"];
export type StayMapPoiResultRaw = components["schemas"]["StayResourceMapPoiResult"];

/** 校验并映射酒店地图搜索响应。 */
export function mapHotelMapSearchResponse(payload: unknown): HotelMapSearchResult | null {
  if (!isRecord(payload) || payload.code !== "OK" || !isRecord(payload.data)) {
    return null;
  }

  const data = payload.data;
  if (Array.isArray(data.hotels)) {
    const hotels = mapResourceHotelSearchItems(data.hotels);
    if (!hotels) {
      return null;
    }
    return { areas: hotels, points: [], degraded: false };
  }
  const areas = mapSearchItems(data.areas, "area");
  const points = mapSearchItems(data.points, "point");
  if (
    !areas
    || !points
    || typeof data.degraded !== "boolean"
    || !isOptionalString(data.cache_version)
    || !isOptionalString(data.refreshed_at)
  ) {
    return null;
  }

  return {
    areas,
    points,
    cacheVersion: data.cache_version ?? undefined,
    refreshedAt: data.refreshed_at ?? undefined,
    degraded: data.degraded,
  };
}

/** 校验并映射酒店地图视口点位响应。 */
export function mapHotelMapPoiResponse(
  payload: unknown,
  viewport: HotelMapViewport,
): HotelMapPoi[] | null {
  if (
    !isRecord(payload)
    || payload.code !== "OK"
    || !isRecord(payload.data)
    || payload.data.coverage_complete !== true
    || !matchesViewportBounds(payload.data.queried_bounds, viewport)
    || !Array.isArray(payload.data.hotels)
  ) {
    return null;
  }

  const hotels: HotelMapPoi[] = [];
  for (const item of payload.data.hotels) {
    const parsed = mapPoi(item);
    if (!parsed) {
      return null;
    }
    hotels.push(parsed);
  }
  return hotels;
}

function mapResourceHotelSearchItems(payload: unknown[]): HotelMapSearchItem[] | null {
  const items: HotelMapSearchItem[] = [];
  for (const item of payload) {
    const parsed = mapResourceHotel(item);
    if (!parsed) {
      return null;
    }
    items.push({ ...parsed, type: "hotel", itineraryCount: 0 });
  }
  return items;
}

function mapResourceHotel(
  item: unknown,
): Omit<HotelMapSearchItem, "type" | "itineraryCount"> | null {
  if (
    !isRecord(item)
    || typeof item.id !== "string"
    || typeof item.name !== "string"
    || typeof item.address !== "string"
    || typeof item.region_text !== "string"
    || !isFiniteNumber(item.lng)
    || !isFiniteNumber(item.lat)
    || !isOptionalString(item.stay_level)
    || !isOptionalString(item.hotel_type)
    || !isGeoVerificationStatus(item.geo_verification_status)
    || !isOptionalFiniteNumber(item.reference_price)
    || !isOptionalString(item.currency_code)
    || !isOptionalString(item.price_type)
    || !isOptionalString(item.price_freshness_level)
    || !isOptionalString(item.price_service_date)
    || !isOptionalString(item.price_sku_name)
  ) {
    return null;
  }
  return {
    id: item.id,
    name: item.name,
    address: item.address,
    regionText: item.region_text,
    lng: item.lng,
    lat: item.lat,
    stayLevel: item.stay_level ?? undefined,
    hotelType: item.hotel_type ?? undefined,
    geoVerificationStatus: item.geo_verification_status,
    referencePrice: item.reference_price ?? undefined,
    currencyCode: item.currency_code ?? undefined,
    priceType: item.price_type ?? undefined,
    priceFreshnessLevel: item.price_freshness_level ?? undefined,
    priceServiceDate: item.price_service_date ?? undefined,
    priceSkuName: item.price_sku_name ?? undefined,
  };
}

function mapSearchItems(
  payload: unknown,
  expectedType: HotelMapSearchItem["type"],
): HotelMapSearchItem[] | null {
  if (!Array.isArray(payload)) {
    return null;
  }

  const items: HotelMapSearchItem[] = [];
  for (const item of payload) {
    const parsed = mapSearchItem(item, expectedType);
    if (!parsed) {
      return null;
    }
    items.push(parsed);
  }
  return items;
}

function mapSearchItem(
  item: unknown,
  expectedType: HotelMapSearchItem["type"],
): HotelMapSearchItem | null {
  if (
    !isRecord(item)
    || typeof item.id !== "string"
    || item.type !== expectedType
    || typeof item.name !== "string"
    || typeof item.region_text !== "string"
    || !isFiniteNumber(item.lng)
    || !isFiniteNumber(item.lat)
    || !isNonNegativeInteger(item.itinerary_count)
  ) {
    return null;
  }

  return {
    id: item.id,
    type: expectedType,
    name: item.name,
    regionText: item.region_text,
    lng: item.lng,
    lat: item.lat,
    itineraryCount: item.itinerary_count,
  };
}

function mapPoi(item: unknown): HotelMapPoi | null {
  if (
    !isRecord(item)
    || typeof item.id !== "string"
    || typeof item.name !== "string"
    || typeof item.address !== "string"
    || !isFiniteNumber(item.lng)
    || !isFiniteNumber(item.lat)
    || (item.typecode !== undefined && typeof item.typecode !== "string")
    || !isOptionalString(item.telephone)
    || !isOptionalString(item.region_text)
    || !isOptionalString(item.stay_level)
    || !isOptionalString(item.hotel_type)
    || !isGeoVerificationStatus(item.geo_verification_status)
    || !isOptionalFiniteNumber(item.reference_price)
    || !isOptionalString(item.currency_code)
    || !isOptionalString(item.price_type)
    || !isOptionalString(item.price_freshness_level)
    || !isOptionalString(item.price_service_date)
    || !isOptionalString(item.price_sku_name)
  ) {
    return null;
  }

  return {
    id: item.id,
    name: item.name,
    address: item.address,
    lng: item.lng,
    lat: item.lat,
    typecode: item.typecode ?? "resource_stay",
    telephone: item.telephone ?? undefined,
    regionText: item.region_text ?? undefined,
    stayLevel: item.stay_level ?? undefined,
    hotelType: item.hotel_type ?? undefined,
    geoVerificationStatus: item.geo_verification_status,
    referencePrice: item.reference_price ?? undefined,
    currencyCode: item.currency_code ?? undefined,
    priceType: item.price_type ?? undefined,
    priceFreshnessLevel: item.price_freshness_level ?? undefined,
    priceServiceDate: item.price_service_date ?? undefined,
    priceSkuName: item.price_sku_name ?? undefined,
  };
}

function matchesViewportBounds(bounds: unknown, viewport: HotelMapViewport): boolean {
  return isRecord(bounds)
    && matchesCoordinate(bounds.southwest, viewport.southwest)
    && matchesCoordinate(bounds.northeast, viewport.northeast);
}

function matchesCoordinate(coordinate: unknown, expected: HotelMapCoordinate): boolean {
  return isRecord(coordinate)
    && isFiniteNumber(coordinate.lng)
    && isFiniteNumber(coordinate.lat)
    && coordinate.lng === expected.lng
    && coordinate.lat === expected.lat;
}

function isOptionalString(value: unknown): value is string | null | undefined {
  return value === undefined || value === null || typeof value === "string";
}

function isOptionalFiniteNumber(value: unknown): value is number | null | undefined {
  return value === undefined || value === null || isFiniteNumber(value);
}

function isGeoVerificationStatus(value: unknown): value is HotelMapGeoVerificationStatus {
  return value === "unverified"
    || value === "pending"
    || value === "resolved"
    || value === "review_required"
    || value === "rejected"
    || value === "failed";
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function isNonNegativeInteger(value: unknown): value is number {
  return isFiniteNumber(value) && Number.isInteger(value) && value >= 0;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
